/**
 * Dialogue blips.
 *
 * Synthesised with a WebAudio oscillator rather than played from files. Two
 * reasons: there are no sound assets to ship or 404, and a blip that is
 * generated can take a pitch argument — Versace is lower than Armane, which is
 * the whole characterisation the design asks for, for free.
 *
 * Mobile browsers block audio until a user gesture. The door tap is that
 * gesture: `unlock()` is called there, and until it has been, every `blip()`
 * is a no-op rather than an error.
 */

type Voice = 'armane' | 'versace' | 'obojica';

/** Hz. Versace lower, deliberately. */
const PITCH: Record<Voice, number> = {
  armane: 420,
  versace: 260,
  obojica: 340,
};

let ctx: AudioContext | null = null;
let muted = false;

export function isMuted(): boolean {
  return muted;
}

export function setMuted(next: boolean): void {
  muted = next;
  try {
    localStorage.setItem('mn.muted', next ? '1' : '0');
  } catch {
    /* storage disabled; the setting just does not persist */
  }
}

export function loadMutePreference(): boolean {
  try {
    muted = localStorage.getItem('mn.muted') === '1';
  } catch {
    muted = false;
  }
  return muted;
}

/** Call from a real user gesture — the door tap. Safe to call repeatedly. */
export function unlock(): void {
  if (ctx) {
    if (ctx.state === 'suspended') void ctx.resume();
    return;
  }
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    ctx = new Ctor();
  } catch {
    // No WebAudio. The site is silent and entirely functional.
    ctx = null;
  }
}

/**
 * The unlocked context, handed to whatever needs to build on the same graph —
 * currently only the microphone analyser at the singing beat.
 *
 * Null until `unlock()` has run, which is the door tap. A caller that finds
 * null should treat audio as unavailable rather than making a second context:
 * a page with two AudioContexts hits the per-page limit on Safari and the
 * second one is the one that silently fails.
 */
export function audioContext(): AudioContext | null {
  return ctx;
}

/**
 * One short square-wave tick. Detuned slightly at random so a line of text
 * does not sound like a modem.
 */
export function blip(voice: Voice): void {
  if (!ctx || muted || ctx.state !== 'running') return;
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'square';
    osc.frequency.value = PITCH[voice] * (0.94 + Math.random() * 0.12);

    // Fast attack, fast decay. Anything longer reads as a tone, not a tick.
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.04, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.055);

    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.06);
  } catch {
    /* audio is never load-bearing */
  }
}

/** The door opening. A low thunk, not a note. */
export function thunk(): void {
  if (!ctx || muted || ctx.state !== 'running') return;
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(140, now);
    osc.frequency.exponentialRampToValueAtTime(45, now + 0.18);
    gain.gain.setValueAtTime(0.14, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.3);
  } catch {
    /* ditto */
  }
}
